import { useEffect, useLayoutEffect, useRef } from "react";
import { useDispatch } from "react-redux";
import { useLocation, useMatches, useNavigationType } from "react-router-dom";

import { pageShown } from "@src/store/slices/navigation";
import { routePattern } from "@src/store/slices/navigation/routePattern";

/** Spread on the side nav, so a click inside it is known to come from the nav. */
export const SIDE_NAV_MARKER = { "data-side-nav": "" };

const entryPointOf = (state: unknown): string | null => {
  if (typeof state !== "object" || state === null) {
    return null;
  }
  const { entryPoint } = state as { entryPoint?: unknown };
  return typeof entryPoint === "string" ? entryPoint : null;
};

/** Records each page the app shows, and how the user got there. */
export const useRecordPageArrivals = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigationType = useNavigationType();
  const matches = useMatches();
  const lastClickInSideNav = useRef(false);

  useEffect(() => {
    const onClick = (event: MouseEvent) => {
      lastClickInSideNav.current =
        event.target instanceof Element &&
        event.target.closest("[data-side-nav]") !== null;
    };
    document.addEventListener("click", onClick, true);
    return () => document.removeEventListener("click", onClick, true);
  }, []);

  const page = routePattern(location.pathname, matches.at(-1)?.params ?? {});
  const recordedPathname = useRef<string | null>(null);
  // A layout effect, so the page is recorded before the new page's own
  // effects read it.
  useLayoutEffect(() => {
    // Only a new page counts; a changed query string on the same page does not.
    if (recordedPathname.current === location.pathname) {
      return;
    }
    recordedPathname.current = location.pathname;
    dispatch(
      pageShown({
        page,
        entryPoint: entryPointOf(location.state),
        // Back and forward are not clicks, whatever was clicked last.
        fromSideNav: navigationType !== "POP" && lastClickInSideNav.current,
      })
    );
    lastClickInSideNav.current = false;
  }, [dispatch, location, navigationType, page]);
};
